import React, { useEffect } from 'react'
import { Role, SubscriptionAndUsersDTO } from '../../../api/schemas/schema'
import { DataContext } from '../../../context/DataContext'
import { useContext } from 'react'
import { Button, makeStyles, tokens, Text, Divider } from '@fluentui/react-components'
import { ArrowDownload24Regular } from '@fluentui/react-icons'
import { ModalActions } from '../../common/modal/Modal'
import { t } from '../../../i18n/i18n'
import { Grid } from '../../common/grid/Grid'
import { GridItem } from '../../common/grid/GridItem'
import { useForm } from 'react-hook-form'
import { IOpenCloseModalProps } from '../../../interfaces/IOpenCloseModalProps'
import { handleMessage } from '../../../helpers/stateHelper'
import { ControlledSubscriptionAccordion } from './ControlledSubscriptionAccordion'
import { api } from '../../../helpers/apiHelper'
import { InfoMessageBar } from '../../common/other/InfoMessageBar'
import { SubscriptionUserRoles } from './SubscriptionUserRoles'
import { RoleGuard } from '../../common/other/RoleGuard'
import { downloadTermsAndConditions } from '../../../helpers/documentHelper'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'
import { getFullName } from '../../../helpers/accountHelper'

const useStyles = makeStyles({
   subscriptionPlan: {
      display: 'flex',
      flexDirection: 'row-reverse',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: tokens.spacingVerticalXS,
   },
   subscriptionTermsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalXXL,
   },
   itemWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXXL,
   },
   itemWrapperNoGap: {
      display: 'flex',
      flexDirection: 'column',
   },
   subscriptionButton: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: tokens.spacingHorizontalS,
   },
   termsButton: {
      marginLeft: 'auto',
   },
   marginBottom: {
      marginBottom: tokens.spacingVerticalXL,
   },
})

export const ModalSubscriptionDetails = ({ open, closeModal }: IOpenCloseModalProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { setRootState } = dataContext
   const { organisation, userAccounts, userAccount } = dataContext.state
   const subscriptionAndUserForm = useForm<SubscriptionAndUsersDTO>({ values: { userAccounts: userAccounts, modules: organisation.modules || [] } })
   const {
      control,
      reset,
      handleSubmit,
      getValues,
      formState: { isDirty = false, isSubmitting },
   } = subscriptionAndUserForm

   const handleSaveClick = handleSubmit(async (data: SubscriptionAndUsersDTO) => {
      try {
         data.userAccounts.forEach((u) => {
            u.modifiedBy = getFullName(userAccount)
         })

         const updatedOrganisationAndUsers = (await api().updateSubscriptionAndUsers(organisation.id, data)).data

         const updatedUserAccount = updatedOrganisationAndUsers.userAccounts.find((u) => u.id === userAccount.id)

         setRootState((prev) => ({
            ...prev,
            userAccount: updatedUserAccount,
            organisation: { ...organisation, modules: updatedOrganisationAndUsers.modules },
            userAccounts: updatedOrganisationAndUsers.userAccounts,
         }))

         closeAndReset()

         handleMessage(setRootState, 'success', t('ItemSuccessfullySaved', { Item: t('Subscription') }))
      } catch (error: any) {
         if (error.status === 403) {
            handleMessage(setRootState, 'error', t('NotAuthorized'), t('NoAccessMessage'))
            return
         }

         handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotUpdateItem', { Item: t('Subscription') }))
      }
   })

   const handleTermsClick = () => {
      downloadTermsAndConditions(dataContext)
   }

   const closeAndReset = () => {
      reset()
      closeModal()
   }

   useEffect(() => {
      if (!open) {
         reset()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open])

   return (
      <>
         <DraggableModal open={open} title={t('ModulesAndUsers')} onClose={closeAndReset}>
            <Form onSubmit={handleSaveClick}>
               <Grid gap="XXXL">
                  <RoleGuard allowedRoles={[Role.SystemAdministrator]}>
                     <GridItem size={'12/12'} className={classes.itemWrapper}>
                        <div className={classes.subscriptionPlan}>
                           <Button appearance="outline" className={classes.termsButton} onClick={handleTermsClick} icon={<ArrowDownload24Regular />}>
                              {t('DownloadTermsAndConditions')}
                           </Button>
                           <Text size={400}>{t('PaymentIsMadeViaMonthlyInvoicing')}</Text>
                        </div>

                        <InfoMessageBar title={t('ContactSalesRepresentativeToOrderUsersAndModules')} />
                        <Divider appearance="brand" />
                     </GridItem>
                  </RoleGuard>

                  <GridItem size={'12/12'} className={classes.itemWrapper}>
                     <Text size={400} weight="semibold">
                        {t('Users')}
                     </Text>

                     <SubscriptionUserRoles control={control} getValues={getValues} disabled={isSubmitting} />

                     <Divider appearance="brand" />
                  </GridItem>

                  <GridItem size={'12/12'} className={classes.itemWrapperNoGap}>
                     <Text size={400} weight="medium">
                        {t('Modules')}
                     </Text>
                     <Text>{t('AllCurrentModulesForOrganisation')}</Text>

                     <ControlledSubscriptionAccordion subscriptionAndUserForm={subscriptionAndUserForm} />
                  </GridItem>

                  <GridItem size={'12/12'}>
                     <ModalActions>
                        <Button
                           onClick={() => {
                              closeAndReset()
                           }}
                           disabled={isSubmitting}
                        >
                           {isDirty ? t('Cancel') : t('Close')}
                        </Button>

                        <Button appearance="primary" type="submit" className={classes.subscriptionButton} disabled={!isDirty || isSubmitting}>
                           {t('Save')}
                        </Button>
                     </ModalActions>
                  </GridItem>
               </Grid>
            </Form>
         </DraggableModal>
      </>
   )
}
